/**
 * ConfirmDialog — imperative confirmation dialog backed by Shadcn AlertDialog.
 *
 * Usage:
 *   1. Wrap your app (or a subtree) with <ConfirmProvider>
 *   2. In any component: const confirm = useConfirm()
 *   3. Call: if (!await confirm({ title, description, destructive })) return;
 */
import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const ConfirmContext = createContext(null);

const DEFAULT_STATE = { open: false, title: '', description: '', destructive: false, resolve: null };

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(DEFAULT_STATE);
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title = 'Are you sure?', description = '', destructive = true } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, title, description, destructive, resolve });
    });
  }, []);

  const handleConfirm = () => {
    setState(DEFAULT_STATE);
    resolveRef.current?.(true);
  };

  const handleCancel = () => {
    setState(DEFAULT_STATE);
    resolveRef.current?.(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription>{state.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={state.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/** Returns the imperative `confirm(opts)` function. Must be inside <ConfirmProvider>. */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
